import { useCallback, useEffect, useRef, useState } from 'react';

import { log } from 'utils/logging';

// We need two layers of caching so that we can cache both the request and the
// response. This allows us to show icons that were preloaded instantly and also
// share the same promise between icons that are still loading.
const svgContentCache: Record<string, string> = {};
const svgContentPromiseCache: Record<string, Promise<string>> = {};

function svgContentPromise(src: string): Promise<string> {
  if (src in svgContentPromiseCache) return svgContentPromiseCache[src];

  // eslint-disable-next-line no-async-promise-executor
  const promise = new Promise<string>(async (resolve) => {
    const response = await fetch(src);
    const content = await response.text();
    resolve(content);

    svgContentCache[src] = content;
    delete svgContentPromiseCache[src];
  });

  svgContentPromiseCache[src] = promise;
  return promise;
}

export function useLoadSvgContent(svgSrc: string): {
  svgSrc: string;
  svgContent: string | undefined;
  loadSvgContent: () => Promise<void>;
} {
  const abortRef = useRef(false);

  /*
   * Because we have cache handling, `useState` doesn't work so well for
   * `svgContent`. We'd need to `useEffect` to reset the content in case `svgSrc`
   * changes.
   *
   * We usually rely on a `ref` to loadSvgContent, if `svgContent` is only updated
   * after rendering, `loadSvgContent` will update but the `<img>` with the
   * ref won't be rendered yet, so there will be no `ref`.
   *
   * To circumvent this issue, we reference the cache directly then have a
   * `useState` to force the component re-render.
   */
  const svgContent =
    svgSrc in svgContentCache ? svgContentCache[svgSrc] : undefined;

  const forceRerenderState = useState(0)[1];

  const loadSvgContent = useCallback(async () => {
    try {
      const content = await svgContentPromise(svgSrc);

      // A component may be unmounted while the SVG content is loading.
      // If this happens, we need to abort setting the `svgContent`
      // because it'll throw an error.
      if (abortRef.current) return;

      if (!content.startsWith('<svg')) {
        throw new Error(`File on path ${svgSrc} that is not an svg `);
      }

      forceRerenderState((counter) => counter + 1);
    } catch (e) {
      log.error(`Failed to lazy load svg: ${(e as Error).message}`);
    }
  }, [forceRerenderState, svgSrc]);

  useEffect(
    () =>
      // Abort the loading process if the component unmounts
      () => {
        abortRef.current = true;
      },
    []
  );

  return { svgSrc, svgContent, loadSvgContent };
}
